import React, { useState, useEffect } from 'react';
import { CircularProgress, Alert, Box } from '@mui/material';
import { ResourceManagementPage } from './ResourceManagementPage';
import { resourceTopicService } from '../services/resourceTopicService';
import type { Category } from '../types';

/**
 * OfficialDocumentsPage - Trang Công Văn
 * 
 * Trang này hiển thị tất cả tài liệu thuộc chủ đề "Công Văn".
 * Sử dụng chung ResourceManagementPage với bộ lọc chủ đề cố định.
 * 
 * Nếu chủ đề "Công Văn" chưa tồn tại, trang sẽ tự động tạo nó.
 */
const OfficialDocumentsPage: React.FC = () => {
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initializeCongVanCategory = async () => {
            try {
                setLoading(true);
                setError(null);

                // Try to find existing "Công Văn" category
                const response = await resourceTopicService.getTopics({
                    page: 1,
                    pageSize: 1000
                });

                const topics = response.categories || [];
                let congVanTopic = topics.find((t: Category) =>
                    t.name.toLowerCase() === 'công văn' ||
                    t.name.toLowerCase() === 'cong van'
                );

                if (!congVanTopic) {
                    // Create "Công Văn" category if it doesn't exist
                    try {
                        congVanTopic = await resourceTopicService.createTopic({
                            name: 'Công Văn',
                            description: 'Công văn, văn bản hành chính'
                        });
                    } catch (createErr: any) {
                        // If creation fails (e.g., already exists due to race condition), try to find again
                        const retryResponse = await resourceTopicService.getTopics({
                            page: 1,
                            pageSize: 1000
                        });
                        const retryTopics = retryResponse.categories || [];
                        congVanTopic = retryTopics.find((t: Category) =>
                            t.name.toLowerCase() === 'công văn' ||
                            t.name.toLowerCase() === 'cong van'
                        );

                        if (!congVanTopic) {
                            throw new Error('Không thể tạo hoặc tìm thấy chủ đề "Công Văn"');
                        }
                    }
                }

                setCategoryId(congVanTopic.id);
            } catch (err: any) {
                console.error('Error initializing Công Văn category:', err);
                setError(err.message || 'Có lỗi khi tải trang Công Văn');
            } finally {
                setLoading(false);
            }
        };

        initializeCongVanCategory();
    }, []);

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // Render ResourceManagementPage with Công Văn category pre-filtered
    return (
        <ResourceManagementPage
            fixedCategoryId={categoryId || undefined}
            fixedCategoryName="Công Văn"
            hideCategoryFilter={true}
            hideChips={true}
        />
    );
};

export default OfficialDocumentsPage;
